import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchUsers } from '../api/users';
import { queryKeys } from '../lib/react-query';

const EMPTY_USERS: UserData[] = [];
const USERS_REQUEST_URL_STORAGE_KEY = 'adminUsersRequestUrl';

const getStoredRequestUrl = () => {
  try {
    return sessionStorage.getItem(USERS_REQUEST_URL_STORAGE_KEY) || 'users/list/';
  } catch {
    return 'users/list/';
  }
};

const getPageNumberFromUrl = (requestUrl: string) => {
  try {
    const parsedUrl = new URL(requestUrl, 'https://app.mtacho.pl');
    const page = parsedUrl.searchParams.get('page');

    return page ? Number(page) || 1 : 1;
  } catch {
    return 1;
  }
};

export interface UserData {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  photo_uri: string;
  role: string;
  is_active: boolean;
  profile_image: string;
  last_login_date?: string | null;
}

const useUsers = () => {
  const [error, setError] = useState<string>('');
  const [requestUrl, setRequestUrl] = useState<string>(getStoredRequestUrl);

  const usersQuery = useQuery({
    queryKey: queryKeys.users.list({ requestUrl }),
    queryFn: () => fetchUsers(requestUrl),
    placeholderData: keepPreviousData,
  });

  const prvPage = usersQuery.data?.previous ?? null;
  const nextPage = usersQuery.data?.next ?? null;
  const allUsers = usersQuery.data?.results ?? EMPTY_USERS;
  const currentPage = useMemo(
    () => getPageNumberFromUrl(requestUrl),
    [requestUrl]
  );

  useEffect(() => {
    if (usersQuery.isError) {
      setError('Wystąpił problem podczas pobierania bazy użytkowników.');
      return;
    }

    setError('');
  }, [usersQuery.isError]);

  useEffect(() => {
    try {
      sessionStorage.setItem(USERS_REQUEST_URL_STORAGE_KEY, requestUrl);
    } catch {
      // Ignore unavailable session storage.
    }
  }, [requestUrl]);

  const getUserList = useCallback(() => {
    setRequestUrl('users/list/');
  }, []);

  const getInactiveUserList = useCallback(() => {
    setRequestUrl('users/list/?is_active=false');
  }, []);

  const goToNextPage = useCallback(() => {
    if (!nextPage || usersQuery.isFetching) {
      return;
    }

    setRequestUrl(nextPage);
  }, [nextPage, usersQuery.isFetching]);

  const goToPreviousPage = useCallback(() => {
    if (!prvPage || usersQuery.isFetching) {
      return;
    }

    setRequestUrl(prvPage);
  }, [prvPage, usersQuery.isFetching]);

  const getParticularUserByFullName = useCallback((fullName: string) => {
    setRequestUrl(`users/list/?full_name=${encodeURIComponent(fullName)}`);
  }, []);

  return {
    error,
    setError,
    getUserList,
    getInactiveUserList,
    getParticularUserByFullName,
    currentPage,
    nextPage,
    prvPage,
    allUsers,
    goToNextPage,
    goToPreviousPage,
    isLoading: usersQuery.isLoading,
    isFetching: usersQuery.isFetching,
  };
};

export default useUsers;
